/**
 * Fortress Aura System
 *
 * Applies passive aura bonuses to turrets and heroes within range of the fortress.
 */
import { FP } from '../fixed.js';
import { getAvailableAuras } from '../data/fortress-auras.js';
import { TURRET_SLOTS } from '../data/turrets.js';
// Cache for aura effects (recalculated when needed)
let cachedAuraEffects = [];
let cacheValidTick = -1;
// ============================================================================
// MAIN UPDATE FUNCTION
// ============================================================================
/**
 * Calculate and cache aura effects for all entities
 * Should be called once per tick before damage calculations
 */
export function updateFortressAuras(state, config) {
    // Only recalculate if cache is stale
    if (cacheValidTick === state.tick)
        return;
    cachedAuraEffects = [];
    // Get fortress position
    const fortressX = config.fortressX;
    const fortressY = FP.fromFloat(7.5); // Center of field height
    // Get available auras for current class and level
    const availableAuras = getAvailableAuras(state.fortressClass, state.commanderLevel);
    // Calculate effects for each hero
    for (const hero of state.heroes) {
        const effects = calculateEntityAuraEffects(hero.x, hero.y, fortressX, fortressY, availableAuras, 'hero');
        if (effects.activeAuras.length > 0) {
            cachedAuraEffects.push({
                entityId: hero.definitionId,
                entityType: 'hero',
                ...effects,
            });
        }
    }
    // Calculate effects for each turret
    for (const turret of state.turrets) {
        const slot = TURRET_SLOTS.find(s => s.id === turret.slotIndex);
        if (!slot)
            continue;
        const turretX = FP.add(config.fortressX, FP.fromFloat(slot.offsetX));
        const turretY = FP.fromFloat(7 + slot.offsetY);
        const effects = calculateEntityAuraEffects(turretX, turretY, fortressX, fortressY, availableAuras, 'turret');
        if (effects.activeAuras.length > 0) {
            cachedAuraEffects.push({
                entityId: turret.slotIndex,
                entityType: 'turret',
                ...effects,
            });
        }
    }
    cacheValidTick = state.tick;
}
/**
 * Calculate aura effects for a single entity
 */
function calculateEntityAuraEffects(entityX, entityY, fortressX, fortressY, auras, entityType) {
    const activeAuras = [];
    const totalModifiers = {};
    const distSq = FP.distSq(entityX, entityY, fortressX, fortressY);
    for (const aura of auras) {
        // Check if entity type matches aura target
        if (aura.targetType !== 'both' && aura.targetType !== entityType) {
            continue;
        }
        // Check if within aura radius
        const radiusSq = FP.mul(aura.radius, aura.radius);
        if (distSq > radiusSq) {
            continue;
        }
        // Entity is affected by this aura
        activeAuras.push({
            auraId: aura.id,
            modifiers: aura.modifiers,
        });
        // Add modifiers to total
        for (const [key, value] of Object.entries(aura.modifiers)) {
            const modKey = key;
            const currentValue = totalModifiers[modKey] ?? 0;
            totalModifiers[modKey] = currentValue + value;
        }
    }
    return { activeAuras, totalModifiers };
}
// ============================================================================
// ACCESSOR FUNCTIONS
// ============================================================================
/**
 * Get aura effects for a specific hero
 */
export function getHeroAuraEffects(heroId) {
    return cachedAuraEffects.find(e => e.entityType === 'hero' && e.entityId === heroId);
}
/**
 * Get aura effects for a specific turret
 */
export function getTurretAuraEffects(slotIndex) {
    return cachedAuraEffects.find(e => e.entityType === 'turret' && e.entityId === slotIndex);
}
/**
 * Get total aura damage bonus for a hero
 */
export function getHeroAuraDamageBonus(heroId) {
    const effects = getHeroAuraEffects(heroId);
    return effects?.totalModifiers.damageBonus ?? 0;
}
/**
 * Get total aura damage bonus for a turret
 */
export function getTurretAuraDamageBonus(slotIndex) {
    const effects = getTurretAuraEffects(slotIndex);
    return effects?.totalModifiers.damageBonus ?? 0;
}
/**
 * Get total aura attack speed bonus for an entity
 */
export function getAuraAttackSpeedBonus(entityType, entityId) {
    const effects = cachedAuraEffects.find(e => e.entityType === entityType && e.entityId === entityId);
    return effects?.totalModifiers.attackSpeedBonus ?? 0;
}
/**
 * Get all active aura effects (for rendering)
 */
export function getAllAuraEffects() {
    return cachedAuraEffects;
}
/**
 * Invalidate aura cache (call when hero/turret positions change significantly)
 */
export function invalidateAuraCache() {
    cacheValidTick = -1;
}
//# sourceMappingURL=fortress-auras.js.map